import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AnalyticsEventName, AnalyticsService, SafeAnalyticsProperties } from './analytics.service';

const PREFIX = 'action-ledgers.analytics.registration.';
const ONCE_KEYS = ['started', 'connection_succeeded', 'connection_failed', 'properties_completed',
  'account_succeeded', 'checkout_returned', 'checkout_canceled', 'checkout_confirmed'] as const;

/** Local deduplication only. No registration, property, or checkout identifiers enter events. */
@Injectable({ providedIn: 'root' })
export class RegistrationFunnelAnalyticsService {
  private readonly analytics = inject(AnalyticsService);

  track(event: AnalyticsEventName, properties: SafeAnalyticsProperties = {}): void {
    this.analytics.track(event, properties);
  }

  once(key: typeof ONCE_KEYS[number], event: AnalyticsEventName, properties: SafeAnalyticsProperties = {}): void {
    if (this.read(key)) return;
    if (this.analytics.track(event, properties)) this.write(key);
  }

  resetAttempt(): void {
    for (const key of ONCE_KEYS) if (key !== 'started') this.remove(key);
  }

  provider(value: string | null | undefined): NonNullable<SafeAnalyticsProperties['provider']> {
    switch (value?.toLowerCase()) {
      case 'ownerrez': return 'ownerrez';
      case 'beds24': return 'beds24';
      default: return 'other';
    }
  }

  countBucket(count: number): NonNullable<SafeAnalyticsProperties['property_count_bucket']> {
    return count <= 1 ? '1' : count <= 5 ? '2-5' : count <= 20 ? '6-20' : '21+';
  }

  errorCategory(error: unknown): NonNullable<SafeAnalyticsProperties['error_category']> {
    if (!(error instanceof HttpErrorResponse)) return 'unknown';
    if (error.status === 0 || error.status >= 500) return 'network';
    if (error.status === 400 || error.status === 422) return 'validation';
    if (error.status === 401 || error.status === 403) return 'authorization';
    if (error.status === 402) return 'payment';
    return 'connection';
  }

  private read(key: string): boolean {
    try { return sessionStorage.getItem(PREFIX + key) === '1'; } catch { return false; }
  }

  private write(key: string): void {
    try { sessionStorage.setItem(PREFIX + key, '1'); } catch { /* Storage may be unavailable. */ }
  }

  private remove(key: string): void {
    try { sessionStorage.removeItem(PREFIX + key); } catch { /* Storage may be unavailable. */ }
  }
}
